import { useEffect, useMemo, useState } from "react";
import { useNavigate, NavLink } from "react-router-dom";
import {
	Rocket,
	MessageCircle,
	Users,
	UserPlus,
	Contact,
	MessageSquareText,
	ArrowRight,
	Check,
	CheckCircle2,
	Loader2,
	X,
	PartyPopper,
} from "lucide-react";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Progress } from "../../components/ui/progress";
import { Badge } from "../../components/ui/badge";
import { cn } from "../../lib/utils";
import { i18n } from "../../translate/i18n";
import { statusOnboarding } from "../../services/onboarding";
import { lerPreferencia, gravarPreferencia } from "../../services/preferencias";

const STORAGE_KEY = "onboarding:skipped:v1";

const ICONES_POR_KEY = {
	whatsapp: MessageCircle,
	queue: Users,
	user: UserPlus,
	contact: Contact,
	ticket: MessageSquareText,
};

const INTENT_POR_KEY = {
	whatsapp: "from-emerald-500 to-teal-500",
	queue: "from-sky-500 to-blue-600",
	user: "from-violet-500 to-fuchsia-500",
	contact: "from-amber-500 to-orange-500",
	ticket: "from-indigo-500 to-indigo-600",
};

function CheckItem({ row, indice, navigate }) {
	const Icon = ICONES_POR_KEY[row.key] || Rocket;
	const gradientBg = INTENT_POR_KEY[row.key] || INTENT_POR_KEY.ticket;
	return (
		<Card
			className={cn(
				"group relative flex flex-col gap-4 overflow-hidden rounded-2xl border p-4 transition-all",
				row.done
					? "border-emerald-500/40 bg-emerald-500/5"
					: "border-border bg-card hover:border-primary/40 hover:shadow-md"
			)}
		>
			<div className="flex items-start gap-4">
				<div
					className={cn(
						"flex h-12 w-12 flex-none items-center justify-center rounded-2xl bg-gradient-to-br text-white shadow-sm",
						gradientBg
					)}
				>
					<Icon className="h-6 w-6" />
				</div>
				<div className="flex min-w-0 flex-1 flex-col gap-1">
					<div className="flex items-center gap-2">
						<span className="text-xs font-medium text-muted-foreground">
							Etapa {indice + 1} de 5
						</span>
						{row.done ? (
							<Badge
								variant="secondary"
								className="bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/20"
							>
								<CheckCircle2 className="mr-1 h-3 w-3" />
								Concluída
							</Badge>
						) : (
							<Badge variant="outline" className="text-xs">
								Pendente
							</Badge>
						)}
					</div>
					<h3 className="truncate text-sm font-semibold">{row.title}</h3>
					<p className="line-clamp-2 text-xs leading-relaxed text-muted-foreground">
						{row.description}
					</p>
					<div className="mt-1 flex items-center gap-2 text-[11px] text-muted-foreground">
						<span className="inline-flex items-center rounded-full border border-border bg-muted/40 px-2 py-0.5">
							{row.count} / {row.required}
						</span>
					</div>
				</div>
				{row.done && (
					<Check className="mt-1 h-5 w-5 flex-none text-emerald-500" />
				)}
			</div>
			{row.cta && (
				<NavLink to={row.cta.to} className="inline-flex w-full">
					<Button
						asChild={false}
						variant={row.done ? "secondary" : "default"}
						size="sm"
						className="w-full justify-between group-hover:translate-x-0.5 transition-transform"
						onClick={() => navigate(row.cta.to)}
					>
						<span className="inline-flex items-center gap-1.5">
							{row.cta.label}
							{!row.done && <ArrowRight className="h-3.5 w-3.5" />}
						</span>
						{row.done ? (
							<Check className="h-3.5 w-3.5" />
						) : (
							<ArrowRight className="h-3.5 w-3.5" />
						)}
					</Button>
				</NavLink>
			)}
		</Card>
	);
}

export default function OnboardingStatus() {
	const navigate = useNavigate();
	const [estado, setEstado] = useState({ carregando: true, dados: null, pulado: false });

	useEffect(() => {
		const pulado = lerPreferencia(STORAGE_KEY, false) === true;
		let cancelado = false;
		async function carregar() {
			setEstado((prev) => ({ ...prev, carregando: true, pulado }));
			try {
				const dados = await statusOnboarding();
				if (!cancelado) setEstado({ carregando: false, dados, pulado });
			} catch (err) {
				if (cancelado) return;
				setEstado({ carregando: false, dados: null, pulado });
			}
		}
		carregar();
		return () => {
			cancelado = true;
		};
	}, []);

	const { carregando, dados, pulado } = estado;

	const progresso = useMemo(() => {
		if (!dados) return 0;
		return Number(dados.progress || 0);
	}, [dados]);
	const concluido = progresso === 100;

	function pular() {
		gravarPreferencia(STORAGE_KEY, true);
		setEstado((prev) => ({ ...prev, pulado: true }));
		navigate("/tickets", { replace: true });
	}

	return (
		<MainContainer>
			<MainHeader>
				<Title label={i18n.t("onboarding.title", "Guia de primeiros passos")} icon={Rocket} />
				<div className="flex items-center gap-2">
					<Button
						variant="ghost"
						size="sm"
						onClick={() => navigate(-1)}
						className="text-muted-foreground"
					>
						<X className="mr-1 h-4 w-4" />
						Voltar
					</Button>
					{!concluido && (
						<Button
							variant="outline"
							size="sm"
							onClick={pular}
							disabled={pulado}
						>
							{pulado ? "Pulado" : "Pular onboarding"}
						</Button>
					)}
					<Button
						variant="default"
						size="sm"
						onClick={() => navigate("/tickets")}
					>
						Ir para atendimentos
						<ArrowRight className="ml-1 h-4 w-4" />
					</Button>
				</div>
			</MainHeader>

			<div className="flex h-full w-full flex-col gap-6 px-6 py-6 overflow-y-auto">
				<Card className="relative overflow-hidden rounded-2xl border bg-gradient-to-br from-primary/10 via-background to-violet-500/10 p-6 dark:from-primary/10 dark:via-background dark:to-violet-500/10">
					<div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
						<div className="flex items-start gap-4">
							<div className="flex h-14 w-14 flex-none items-center justify-center rounded-2xl bg-gradient-to-br from-primary to-indigo-500 text-white shadow-lg">
								{concluido ? (
									<PartyPopper className="h-7 w-7" />
								) : carregando ? (
									<Loader2 className="h-7 w-7 animate-spin" />
								) : (
									<Rocket className="h-7 w-7" />
								)}
							</div>
							<div className="flex flex-col gap-1.5">
								<h2 className="text-lg font-semibold leading-tight">
									{concluido
										? "🎉 Parabéns! Seu Perabit está 100% pronto para atender."
										: carregando
											? "Carregando status de onboarding…"
											: pulado
												? "Você pulou o onboarding. Mas não tem problema — as etapas continuam aqui."
												: `Só mais ${dados?.pendingCount || 0} passos para deixar o CRM redondo.`}
								</h2>
								<p className="max-w-2xl text-sm text-muted-foreground leading-relaxed">
									Complete as 5 etapas para operar sem dor de cabeça. Quando tudo
									estiver ok, seu time já pode atender no automático.
								</p>
							</div>
						</div>
						<div className="flex w-full max-w-md flex-col gap-2">
							<div className="flex items-baseline justify-between">
								<span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
									Progresso total
								</span>
								<span className="text-lg font-semibold tabular-nums">
									{progresso}%
								</span>
							</div>
							<Progress
								value={progresso}
								className="h-2.5 w-full"
								indicatorClassName={cn(
									concluido
										? "bg-gradient-to-r from-emerald-500 to-teal-500"
										: "bg-gradient-to-r from-primary to-indigo-500"
								)}
							/>
							<div className="flex items-center justify-between text-xs text-muted-foreground">
								<span>
									{dados?.doneCount || 0} de {dados?.totalChecks || 5} etapas
									concluídas
								</span>
								<span>
									{dados?.pendingCount || 5} pendentes
								</span>
							</div>
						</div>
					</div>
				</Card>

				{carregando ? (
					<div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
						{Array.from({ length: 5 }).map((_, i) => (
							<Card
								key={i}
								className="flex flex-col gap-4 rounded-2xl border p-4"
							>
								<div className="flex items-start gap-4">
									<div className="h-12 w-12 flex-none animate-pulse rounded-2xl bg-muted" />
									<div className="flex-1 space-y-2">
										<div className="h-3 w-20 animate-pulse rounded bg-muted" />
										<div className="h-4 w-48 animate-pulse rounded bg-muted" />
										<div className="h-3 w-64 animate-pulse rounded bg-muted" />
									</div>
								</div>
								<div className="h-8 w-full animate-pulse rounded-lg bg-muted" />
							</Card>
						))}
					</div>
				) : !dados ? (
					<Card className="flex flex-col items-center justify-center rounded-2xl p-10 text-center">
						<p className="text-destructive">
							Erro ao carregar onboarding.
						</p>
						<Button
							size="sm"
							className="mt-4"
							onClick={() => navigate(0)}
						>
							Recarregar página
						</Button>
					</Card>
				) : (
					<div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
						{dados.checks.map((row, i) => (
							<CheckItem
								key={row.key}
								row={row}
								indice={i}
								navigate={navigate}
							/>
						))}
					</div>
				)}

				{concluido && !carregando && dados && (
					<Card className="flex flex-col items-start gap-3 rounded-2xl border-2 border-emerald-500/40 bg-emerald-500/5 p-6 md:flex-row md:items-center md:justify-between">
						<div className="flex items-start gap-3">
							<div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-emerald-500/20 text-emerald-600 dark:text-emerald-400">
								<PartyPopper className="h-5 w-5" />
							</div>
							<div>
								<h3 className="text-sm font-semibold text-emerald-700 dark:text-emerald-300">
									Tudo pronto!
								</h3>
								<p className="max-w-2xl text-xs text-emerald-700/80 dark:text-emerald-300/80">
									Você completou todas as 5 etapas. Agora é só atender. As etapas
									continuam aqui para consulta se o time crescer no futuro.
								</p>
							</div>
						</div>
						<Button
							variant="default"
							onClick={() => navigate("/tickets")}
						>
							Começar a atender
							<ArrowRight className="ml-1 h-4 w-4" />
						</Button>
					</Card>
				)}
			</div>
		</MainContainer>
	);
}
